import { useNavigate, useParams } from "react-router-dom";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { FilterAlt, Search, Sort } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { RxPlusCircled, RxUpdate } from "react-icons/rx";
import { BsCartPlusFill } from "react-icons/bs";
import { cloneDeep } from "lodash";
import { useMutation } from "@tanstack/react-query";

import useDebounce from "../../hooks/useDebounce";
import {
  activateProducts,
  fetchProductList,
} from "../../../services/InventoryAPI";
import InventoryNavigation from "./InventoryNavigation";
import { INVENTORY_STATUS, sortOptions } from "../../constants/constant";
import JumpPagePagination from "../../common/particles.jsx/JumpPagePagination";
import ProductCard from "./inventoryComponent/ProductCard";
import ProductExpired from "./productExpired/ProductExpired";
import ModalCategoryFilter from "./inventoryComponent/ModalCategoryFilter";
import ModalLabelFilter from "./inventoryComponent/ModalLabelFilter";
import ModalPrincipalFilter from "./inventoryComponent/ModalPrincipalFilter";
import ModalDeactivateProduct from "../../common/particles.jsx/ModalDeactivateProduct";
import ModalDeleteProducts from "../../common/particles.jsx/ModalDeleteProducts";
import ModalActivateProducts from "../../common/particles.jsx/ModalActivateProducts";

const InventoryList = ({ isWarehouse }) => {
  const { status } = useParams();
  const navigate = useNavigate();

  const INITIAL_BODY = {
    offset: 1,
    limit: 10,
    status: Number(status),
    type: "",
    list_id: [],
    name: "",
    sort: sortOptions[0],
    warehouse: isWarehouse ? isWarehouse : false,
  };

  const [productId, setProductId] = useState([]);
  const [search, setSearch] = useState("");
  const [reqBody, setReqBody] = useState(INITIAL_BODY);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElProduct, setAnchorElProduct] = useState(null);
  const [anchorElSort, setAnchorElSort] = useState(null);
  const [openModalAction, setOpenModalAction] = useState(null);
  const [openCategoryFilter, setOpenCategoryFilter] = useState(false);
  const [openLabelFilter, setOpenLabelFilter] = useState(false);
  const [openPrincipalFilter, setOpenPrincipalFilter] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openActivateModal, setOpenActivateModal] = useState(false);

  const debounce = useDebounce(search, 500);

  const refetchList = () => {
    setReqBody(INITIAL_BODY);
    mutateList(INITIAL_BODY);
  };

  const {
    mutate: mutateList,
    data: dataList,
    isLoading: isPendingList,
  } = useMutation({
    mutationFn: fetchProductList,
  });

  const { mutate: mutateActivateProduct } = useMutation({
    mutationFn: activateProducts,
    onSuccess: () => {
      setOpenActivateModal(false);
      refetchList();
    },
  });

  const handleSelectList = useCallback(
    (selectedId) => {
      const selectedValue = productId.findIndex((id) => id === selectedId);
      let updatedList = cloneDeep(productId);

      if (selectedValue !== -1) {
        updatedList.splice(selectedValue, 1);
      } else {
        updatedList.push(selectedId);
      }
      setProductId(updatedList);
    },
    [productId]
  );

  const SelectAllComponent = () => {
    return (
      <div className="flex justify-between items-center mb-2">
        <FormControlLabel
          control={
            <Checkbox
              disabled={
                dataList?.product_data?.length === 0 ||
                !dataList ||
                (dataList && !dataList.product_data)
              }
              checked={
                dataList?.product_data?.length > 0 &&
                dataList.product_data.every((item) =>
                  productId.includes(item.product_id)
                )
              }
              onChange={(e) => {
                if (e.target.checked) {
                  setProductId(
                    dataList.product_data.map((item) => item.product_id)
                  );
                } else {
                  setProductId([]);
                }
              }}
            />
          }
          label="Pilih Semua Produk"
        />
        <div>
          {isPendingList ? (
            <CircularProgress size={20} />
          ) : (
            <p className="text-[14px]">
              {dataList && dataList.jumlah_product
                ? dataList.jumlah_product
                : 0}{" "}
              produk
            </p>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (status) {
      if (INVENTORY_STATUS.map((item) => item.oldNav).includes(status)) {
        navigate(
          `${isWarehouse ? "/central-warehouse" : ""}/inventory/${
            INVENTORY_STATUS.find((item) => item.oldNav === status).status
          }`
        );
      } else {
        setSearch("");
        setReqBody(() => ({ ...INITIAL_BODY, status: Number(status) }));
        sessionStorage.setItem("inventoryStatus", Number(status));
      }
    } else navigate(`${isWarehouse ? "/central-warehouse" : ""}/inventory/1`);
  }, [status]);

  useEffect(() => {
    sessionStorage.setItem("id", productId);
  }, [productId]);

  useEffect(() => {
    setReqBody({
      ...INITIAL_BODY,
      warehouse: isWarehouse ? isWarehouse : false,
    });
  }, [isWarehouse]);

  useEffect(() => {
    mutateList({
      ...reqBody,
      sort: reqBody.sort?.value,
      name: debounce,
    });
  }, [reqBody, debounce]);

  return (
    <div>
      {Number(status) === 1 ? (
        <div className="flex justify-end gap-2">
          <button
            className="p-2 bg-green-500 px-4 font-medium text-white !rounded-md hover:bg-green-300"
            onClick={() =>
              navigate(
                `${isWarehouse ? "/central-warehouse" : ""}/inventory/export`
              )
            }
          >
            Export Product
          </button>
          <button
            className="p-2 bg-blue-500 px-4 font-medium text-white !rounded-md hover:bg-blue-300"
            onClick={() =>
              navigate(
                `${isWarehouse ? "/central-warehouse" : ""}/inventory/import`
              )
            }
          >
            Import Product
          </button>
          <div></div>
        </div>
      ) : null}
      <InventoryNavigation isWarehouse={isWarehouse ? isWarehouse : false} />

      <div>
        {Number(status) === 5 ? (
          <div className="mt-4 mb-2">
            <ProductExpired isWarehouse={isWarehouse} />
          </div>
        ) : (
          <div>
            <div className="mt-4 mb-2 flex gap-2 items-center">
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                placeholder="Cari Produk"
                InputProps={{
                  startAdornment: (
                    <InputAdornment>
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="w-full overflow-x-auto mb-2">
              <div className="flex gap-2 items-center justify-end min-w-max">
                <button
                  type="button"
                  className="hover:bg-gray-300 py-[9px] !rounded-md px-4 border flex items-center gap-2 flex gap-2 items-center"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <FilterAlt />
                  <p>Filter</p>
                </button>
                {Number(status) === 1 || Number(status) === 3 ? (
                  <button
                    type="button"
                    className="hover:bg-gray-300 bg-blue-500 text-white py-[10px] px-4 !rounded-md flex items-center gap-2"
                    onClick={() => {
                      if (productId.length === 0 && Number(status) === 3)
                        navigate(
                          `${
                            isWarehouse ? "/central-warehouse" : ""
                          }/inventory/update-stock-habis`
                        );
                      else
                        navigate(
                          `${
                            isWarehouse ? "/central-warehouse" : ""
                          }/inventory/update-stock-several`
                        );
                    }}
                  >
                    <RxUpdate />
                    <p className="whitespace-nowrap">Update stock dan Harga</p>
                  </button>
                ) : null}
                {Number(status) === 1 ? (
                  <button
                    type="button"
                    className={`bg-orange-500 hover:bg-gray-300 text-white py-[10px] px-4 !rounded-md flex items-center gap-2`}
                    onClick={() => {
                      setProductId([]);
                      navigate(
                        `${
                          isWarehouse ? "/central-warehouse" : ""
                        }/inventory/tambah-produk`
                      );
                    }}
                  >
                    <RxPlusCircled />
                    <p className="whitespace-nowrap">Tambah Produk</p>
                  </button>
                ) : null}
                {Number(status) === 3 ? (
                  <button
                    className="bg-orange-500 text-white hover:bg-gray-100 py-[10px] px-4 !rounded-md inline-flex items-center gap-2 disabled:bg-gray-300"
                    disabled={productId.length === 0}
                    onClick={() =>
                      navigate(
                        `${
                          isWarehouse ? "/central-warehouse" : ""
                        }/inventory/create-purchase`
                      )
                    }
                  >
                    <BsCartPlusFill />
                    <p className="whitespace-nowrap">Buat Pembelian</p>
                  </button>
                ) : null}
                {Number(status) === 2 ? (
                  <>
                    <button
                      className={`bg-blue-500 text-white hover:bg-gray-300 py-[10px] px-4 !rounded-md inline-flex items-center gap-2 disabled:bg-gray-300`}
                      onClick={() => setOpenDeleteModal(true)}
                      disabled={productId.length === 0}
                    >
                      <p className="whitespace-nowrap">Hapus</p>
                    </button>
                    <button
                      className={`bg-orange-500 text-white hover:bg-gray-300 py-[10px] px-4 !rounded-md inline-flex items-center gap-3 disabled:bg-gray-300`}
                      onClick={() => setOpenActivateModal(true)}
                      disabled={productId.length === 0}
                    >
                      <p className="whitespace-nowrap">Activate</p>
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className={`hover:bg-gray-100 border border-black py-[8px] px-4 !rounded-md flex items-center gap-2`}
                  aria-controls={
                    Boolean(anchorElSort) ? "basic-menu" : undefined
                  }
                  aria-haspopup="true"
                  aria-expanded={Boolean(anchorElSort) ? "true" : undefined}
                  onClick={(e) => setAnchorElSort(e.currentTarget)}
                >
                  <Sort />
                  <p className="whitespace-nowrap">{reqBody.sort?.name}</p>
                </button>
              </div>
            </div>

            <div className="mb-4">
              <SelectAllComponent />

              {dataList &&
              !isPendingList &&
              dataList.product_data &&
              dataList.jumlah_product > 0 ? (
                dataList.product_data.map((item, i) => {
                  return (
                    <div>
                      <ProductCard
                        item={item}
                        productId={productId}
                        setProductId={setProductId}
                        setAnchorElProduct={setAnchorElProduct}
                        handleSelectList={handleSelectList}
                        inventoryStatus={Number(status)}
                        isWarehouse={isWarehouse}
                      />
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center justify-center">
                  {isPendingList ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Tidak ada produk"
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {dataList && !isPendingList && dataList.jumlah_product > 0 ? (
          <div className="mb-4">
            <JumpPagePagination
              offset={reqBody.offset}
              limit={reqBody.limit}
              setOffset={(e) =>
                setReqBody((prevVal) => ({ ...prevVal, offset: e }))
              }
              setLimit={(e) => {
                setReqBody((prevVal) => ({ ...prevVal, limit: e }));
              }}
              total={dataList.jumlah_product}
              canChangeLimit={true}
            />
          </div>
        ) : null}
      </div>

      {/* Dropdown Sort */}
      <Menu
        anchorEl={anchorElSort}
        open={Boolean(anchorElSort)}
        onClose={() => setAnchorElSort(null)}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {sortOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              setAnchorElSort(null);
              setReqBody((prevVal) => ({ ...prevVal, sort: option }));
            }}
            sx={{
              background: reqBody.sort.value === option.value ? "#f3f4f6" : "",
            }}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Pop up differs per status
      status selain 2 & 5 -> kategori, golongan, principal -> pop up
      */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={() => {
            setOpenCategoryFilter(true);
            setReqBody((prevVal) => ({ ...prevVal, list_id: [] }));
          }}
        >
          Kategori
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenLabelFilter(true);
            setReqBody((prevVal) => ({ ...prevVal, list_id: [] }));
          }}
        >
          Golongan
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenPrincipalFilter(true);
            setReqBody((prevVal) => ({ ...prevVal, list_id: [] }));
          }}
        >
          Principal
        </MenuItem>
      </Menu>

      {/* Dropdown from product card */}
      <Menu
        id="basic-menu"
        anchorEl={anchorElProduct}
        open={Boolean(anchorElProduct)}
        onClose={() => setAnchorElProduct(null)}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {Number(status) === 1 ||
        Number(status) === 3 ||
        Number(status) === 4 ? (
          <MenuItem
            onClick={() => {
              if (productId.length === 0 && Number(status) === 3)
                navigate(
                  `${
                    isWarehouse ? "/central-warehouse" : ""
                  }/inventory/update-stock-habis`
                );
              else
                navigate(
                  `${
                    isWarehouse ? "/central-warehouse" : ""
                  }/inventory/update-stock-several`
                );
            }}
          >
            Update stock & Harga
          </MenuItem>
        ) : null}
        {Number(status) === 1 ? (
          <MenuItem
            onClick={() => {
              setAnchorElProduct(null);
              setOpenDeactivateModal(true);
            }}
          >
            Deaktivasi
          </MenuItem>
        ) : null}
        {Number(status) === 3 ? (
          <MenuItem
            onClick={() =>
              navigate(
                `${
                  isWarehouse ? "/central-warehouse" : ""
                }/inventory/create-purchase`
              )
            }
          >
            Buat Pembelian
          </MenuItem>
        ) : null}
        {Number(status) === 2 ? (
          <MenuItem
            onClick={() => {
              setAnchorElProduct(null);
              setOpenActivateModal(true);
            }}
          >
            Aktivasi
          </MenuItem>
        ) : null}
        {Number(status) === 1 || Number(status) === 2 ? (
          <MenuItem
            onClick={() => {
              setAnchorElProduct(null);
              setOpenDeleteModal(true);
            }}
          >
            Hapus
          </MenuItem>
        ) : null}
      </Menu>

      <ModalCategoryFilter
        open={openCategoryFilter}
        setOpen={setOpenCategoryFilter}
        listId={reqBody.list_id}
        setListId={(e) => setReqBody((prevVal) => ({ ...prevVal, list_id: e }))}
        setType={(e) => setReqBody((prevVal) => ({ ...prevVal, name: e }))}
        offset={reqBody.offset}
      />

      <ModalPrincipalFilter
        open={openPrincipalFilter}
        setOpen={setOpenPrincipalFilter}
        listId={reqBody.list_id}
        setListId={(e) => setReqBody((prevVal) => ({ ...prevVal, list_id: e }))}
        setType={(e) => setReqBody((prevVal) => ({ ...prevVal, name: e }))}
        offset={reqBody.offset}
      />

      <ModalLabelFilter
        open={openLabelFilter}
        setOpen={setOpenLabelFilter}
        listId={reqBody.list_id}
        setListId={(e) => setReqBody((prevVal) => ({ ...prevVal, list_id: e }))}
        setType={(e) => setReqBody((prevVal) => ({ ...prevVal, name: e }))}
      />

      <ModalDeactivateProduct
        open={openDeactivateModal}
        setOpen={setOpenDeactivateModal}
        productId={productId}
        warehouse={isWarehouse ? isWarehouse : false}
      />
      <ModalDeleteProducts
        isWarehouse={isWarehouse}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        productId={productId}
      />
      <ModalActivateProducts
        open={openActivateModal}
        setOpen={setOpenActivateModal}
        productId={productId}
        mutateActivateProduct={mutateActivateProduct}
        isWarehouse={isWarehouse}
      />
    </div>
  );
};

export default InventoryList;
